/**
 * Frontend authz module.
 *
 * The CASL ability itself (rules, subject detection, root-admin semantics) is
 * defined ONCE in `@redirector/commons` and consumed verbatim here. This file
 * only adds frontend-specific helpers — the kind of "can I do X anywhere?"
 * and "which memberships grant Y?" queries that gate UI (action buttons,
 * group selectors) and don't fit CASL's per-resource model.
 *
 * NEVER duplicate authz logic here: if the backend and frontend disagree,
 * the user either sees buttons that 403 or fails to see actions they have.
 * Put new rules in the shared package.
 */
import {
  type AppAbility,
  type AppAction,
  type AppSubject,
  createEmptyAbility,
  defineAbilityFor,
  detectSubjectType,
  isRootAdmin,
} from '@redirector/commons';
import { ROLE_ACTIONS } from '@redirector/commons';
import type { Membership, ResourceType, Role, RoleAction, User } from '@redirector/front/types/types';

// Re-export the shared surface so consumers have one import site.
export {
  createEmptyAbility,
  defineAbilityFor,
  detectSubjectType,
  isRootAdmin,
};
export type { AppAbility, AppAction, AppSubject };

// ── Frontend-only UI helpers ───────────────────────────────────────────────
//
// These are NOT authz rules. They are view-layer queries that ask "does the
// user have this permission SOMEWHERE?" so we can show or hide buttons. The
// backend does the real enforcement through CASL.

/**
 * Can the user perform `action` on `resourceType` in AT LEAST ONE of their
 * active memberships? Gates action buttons (e.g. "Create Redirection") that
 * don't have a specific resource in hand yet. Root admins get `true` for
 * free, consistent with their `manage all` rule.
 */
export function canAnywhere(
  user: Pick<User, 'memberships'> | null | undefined,
  action: RoleAction,
  resourceType: ResourceType,
): boolean {
  if (!user?.memberships) return false;
  if (isRootAdmin(user)) return true;
  for (const m of user.memberships) {
    if (m.isDisabled) continue;
    for (const role of m.roles) {
      if (role.isDisabled) continue;
      if (role.resourceType === resourceType && role.actions.includes(action)) return true;
    }
  }
  return false;
}

/**
 * Return the active memberships where the user actually holds the given
 * permission. Used to populate group pickers (e.g. "create a redirection
 * IN which of my groups?").
 */
export function membershipsWith(
  user: Pick<User, 'memberships'> | null | undefined,
  action: RoleAction,
  resourceType: ResourceType,
): Membership[] {
  if (!user?.memberships) return [];
  const out: Membership[] = [];
  for (const m of user.memberships) {
    if (m.isDisabled) continue;
    const ok = m.roles.some(
      (r) => !r.isDisabled && r.resourceType === resourceType && r.actions.includes(action),
    );
    if (ok) out.push(m);
  }
  return out;
}

/** Role list filter: subset of `roles` with matching resourceType. */
export function rolesOfType(roles: Role[], type: ResourceType): Role[] {
  return roles.filter((r) => r.resourceType === type);
}

/**
 * Typed wrapper around `ability.can(action, subjectObject)`.
 *
 * Why this exists:
 *   CASL's `MongoAbility#can` overloads don't accept arbitrary domain
 *   objects in their TypeScript surface — only the subject literal
 *   (`'redirection'`, `'group'`, ...) or a class registered with
 *   `forcedValue.__caslSubjectType__`. At runtime, however, CASL routes
 *   the object through our shared `detectSubjectType`, which reads
 *   `__typename` (GraphQL) or `constructor.modelName` (Mongoose) and
 *   maps them to the lowercase subject keys. The behaviour is correct;
 *   only the types disagree.
 *
 *   Rather than scattering `as any` casts at every call site (which
 *   trips the no-explicit-any rule), funnel the cast here — once,
 *   commented, and named — and give consumers a normal typed function.
 */
export function canDo(
  ability: AppAbility,
  action: AppAction,
  subject: object,
): boolean {
  return (ability.can as (a: AppAction, s: unknown) => boolean)(action, subject);
}

/**
 * Does the user have ANY action (read/create/update/delete) on this resource
 * in at least one active group? This is the uniform rule used to gate:
 *
 *   - Header tab visibility
 *   - Top-level page routes (/users, /redirections, ...)
 *
 * Rationale: if a user can do anything with users in some group (e.g. they
 * are a user_admin on 'engineering'), the Users tab and page must be
 * reachable. The backend list endpoints then scope the actual data to the
 * groups the user can see. Gating on `read` alone would hide pages from
 * users with write-but-not-read roles, which would be a surprising dead-end.
 *
 * Root admins always get `true`.
 */
export function hasAnyAccess(
  user: Pick<User, 'memberships'> | null | undefined,
  resourceType: ResourceType,
): boolean {
  if (!user?.memberships) return false;
  if (isRootAdmin(user)) return true;
  return ROLE_ACTIONS.some((a) => canAnywhere(user, a, resourceType));
}
